import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  Alert,
  AlertBody,
  AlertIcon,
} from '@lifeomic/chroma-react/components/Alert';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Info } from '@lifeomic/chromicons';
import { getTestProps } from '@lifeomic/phc-web-toolkit/src/util/testProps';
import { type MemberContactField } from './memberContactEdit';
import {
  type ContactFieldSyncStatus,
  getContactSyncIssue,
} from './memberContactSync';

const messages = defineMessages({
  emailLabel: {
    id: 'MemberContactSync.emailLabel',
    defaultMessage: 'email',
  },
  phoneLabel: {
    id: 'MemberContactSync.phoneLabel',
    defaultMessage: 'phone',
  },
  notSet: {
    id: 'MemberContactSync.notSet',
    defaultMessage: 'not set',
  },
  mismatchTitle: {
    id: 'MemberContactSync.mismatchTitle',
    defaultMessage:
      "The Elation {field} and the platform (app login) {field} don't match:",
  },
  elationValueLine: {
    id: 'MemberContactSync.elationValueLine',
    defaultMessage: 'Elation {field}: {value}',
  },
  appLoginValueLine: {
    id: 'MemberContactSync.appLoginValueLine',
    defaultMessage: 'Platform (app login) {field}: {value}',
  },
  mismatchGuidance: {
    id: 'MemberContactSync.mismatchGuidance',
    defaultMessage:
      'If the Elation {field} is correct, choose Sync {field} to overwrite the app login with it. If the app login {field} is correct, choose Edit {field} and enter it — that updates both.',
  },
  mismatchGuidanceEditOnly: {
    id: 'MemberContactSync.mismatchGuidanceEditOnly',
    defaultMessage:
      'There is no Elation {field} to sync from. Choose Edit {field} and enter the correct value — that updates both.',
  },
  elationCaveatEmail: {
    id: 'MemberContactSync.elationCaveatEmail',
    defaultMessage:
      'Neither action changes Elation, and editing the Elation chart will not change these — once a member is onboarded, their email is no longer synced from Elation.',
  },
  elationCaveatPhone: {
    id: 'MemberContactSync.elationCaveatPhone',
    defaultMessage:
      'Neither action changes Elation, and a later Elation sync can overwrite the platform phone.',
  },
  unverifiedReason: {
    id: 'MemberContactSync.unverifiedReason',
    defaultMessage:
      'The platform (app login) {field} ({value}) matches Elation but has not been verified.',
  },
  unverifiedGuidance: {
    id: 'MemberContactSync.unverifiedGuidance',
    defaultMessage:
      'Choose Verify {field} to mark it verified. The value itself does not change.',
  },
  syncButton: {
    id: 'MemberContactSync.syncButton',
    defaultMessage: 'Sync {field}',
  },
  verifyButton: {
    id: 'MemberContactSync.verifyButton',
    defaultMessage: 'Verify {field}',
  },
  editButton: {
    id: 'MemberContactSync.editButton',
    defaultMessage: 'Edit {field}',
  },
});

export type MemberContactSyncIndicatorProps = {
  field: MemberContactField;
  status: ContactFieldSyncStatus;
  onSync: () => void;
  /** Opens the contact edit modal, which writes both the platform record and the app login. */
  onEdit: () => void;
  isSyncing: boolean;
};

/**
 * Surfaces when a member's app-login email/phone is out of sync with the
 * platform record (content mismatch or unverified). Names both values and both
 * remediation paths: Sync pushes the platform (Elation-sourced) value onto the
 * app login, Edit sets both to a new value. Renders nothing when in sync.
 */
export const MemberContactSyncIndicator: React.FC<
  MemberContactSyncIndicatorProps
> = ({ field, status, onSync, onEdit, isSyncing }) => {
  const { formatMessage } = useIntl();
  const issue = getContactSyncIssue(status);
  if (!issue) {
    return null;
  }

  const fieldLabel = formatMessage(
    field === 'phone' ? messages.phoneLabel : messages.emailLabel,
  );
  const notSet = formatMessage(messages.notSet);
  // Syncing an email with no platform value is rejected by member-operations;
  // for phone the same action legitimately clears the app-login number.
  const canSync = field === 'phone' || Boolean(status.snapshot?.trim());

  return (
    <Alert
      statusType="warning"
      fullWidth
      {...getTestProps(`contact-sync-${field}`)}
    >
      <AlertIcon icon={Info} />
      <AlertBody>
        <Box direction="column" gap={1}>
          {issue === 'content' ? (
            <>
              <Text size="subbody">
                {formatMessage(messages.mismatchTitle, { field: fieldLabel })}
              </Text>
              <Text size="subbody">
                {formatMessage(messages.elationValueLine, {
                  field: fieldLabel,
                  value: status.snapshot?.trim() || notSet,
                })}
              </Text>
              <Text size="subbody">
                {formatMessage(messages.appLoginValueLine, {
                  field: fieldLabel,
                  value: status.appLogin?.trim() || notSet,
                })}
              </Text>
              <Text size="subbody">
                {formatMessage(
                  canSync
                    ? messages.mismatchGuidance
                    : messages.mismatchGuidanceEditOnly,
                  { field: fieldLabel },
                )}
              </Text>
              <Text size="subbody">
                {formatMessage(
                  field === 'phone'
                    ? messages.elationCaveatPhone
                    : messages.elationCaveatEmail,
                )}
              </Text>
            </>
          ) : (
            <>
              <Text size="subbody">
                {formatMessage(messages.unverifiedReason, {
                  field: fieldLabel,
                  value: status.appLogin?.trim() || notSet,
                })}
              </Text>
              <Text size="subbody">
                {formatMessage(messages.unverifiedGuidance, {
                  field: fieldLabel,
                })}
              </Text>
            </>
          )}
          <Box direction="row" align="center" gap={1} flexWrap>
            {canSync ? (
              <Button
                variant="text"
                disabled={isSyncing}
                onClick={onSync}
                {...getTestProps(`contact-sync-action-${field}`)}
              >
                {formatMessage(
                  issue === 'unverified'
                    ? messages.verifyButton
                    : messages.syncButton,
                  { field: fieldLabel },
                )}
              </Button>
            ) : null}
            {issue === 'content' ? (
              <Button
                variant="text"
                disabled={isSyncing}
                onClick={onEdit}
                {...getTestProps(`contact-sync-edit-action-${field}`)}
              >
                {formatMessage(messages.editButton, { field: fieldLabel })}
              </Button>
            ) : null}
          </Box>
        </Box>
      </AlertBody>
    </Alert>
  );
};
